import { useEffect, useRef } from 'react';
// @ts-ignore
import { HiddenWebview } from 'capacitor-hidden-webview';
import { PluginListenerHandle } from '@capacitor/core';

export function ApplePayWidget({
  paymentUrl,
  setIsProcessingPayment,
  setTransactionStatus,
  onAlert,
  forceFeature = false,
}: {
  paymentUrl: string;
  setIsProcessingPayment?: (loading: boolean) => void;
  setTransactionStatus?: (
    status: 'pending' | 'success' | 'error' | null,
  ) => void;
  onAlert?: (
    title: string,
    message: string,
    type: 'success' | 'error' | 'info',
  ) => void;
  forceFeature: boolean;
}) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const finalUrl = `${paymentUrl}${forceFeature ? '&forceFeature=true' : ''}`;

  // Create hidden webview once
  useEffect(() => {
    if (!paymentUrl) return;

    (async () => {
      // Create hidden webview off-screen
      await HiddenWebview.create({
        url: finalUrl,
        injectScript: `
          (function(){
            window.addEventListener('message', e => {
              if(window.webkit?.messageHandlers?.hiddenBridge){
                window.webkit.messageHandlers.hiddenBridge.postMessage(e.data);
              }
              if(window.AndroidHiddenBridge){
                AndroidHiddenBridge.postMessage(JSON.stringify(e.data));
              }
            });
          })();
        `,
      });

      // Set 30 s load timeout
      timeoutRef.current = setTimeout(() => {
        onAlert?.(
          'Payment Timeout',
          'The payment process took too long. Please try again.',
          'error',
        );
        setIsProcessingPayment?.(false);
      }, 30000);
    })();

    // cleanup on unmount
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      HiddenWebview.destroy();
    };
  }, [paymentUrl]);

  // Listen for messages from webview
  useEffect(() => {
    let listener: PluginListenerHandle | null = null;
    const setupListener = async () => {
      HiddenWebview.addListener('webviewMessage', (ev) => {
        try {
          const data =
            typeof ev.data === 'string' && ev.data !== 'recaptcha-setup'
              ? JSON.parse(ev.data)
              : ev.data;
          const { eventName } = data;
          console.log('Event:', eventName);

          switch (eventName) {
            case 'onramp_api.load_success':
              HiddenWebview.evaluateJavascript({
                js: `
                const style=document.createElement('style');
                style.textContent=\`apple-pay-button{display:none!important;}\`;
                document.head.appendChild(style);
                const btn=document.getElementById('api-onramp-apple-pay-button');
                if(btn) btn.click();
              `,
              });
              break;

            case 'onramp_api.cancel':
              onAlert?.(
                'Payment Cancelled',
                'The payment was cancelled by the user',
                'info',
              );
              setIsProcessingPayment?.(false);
              break;

            case 'onramp_api.commit_error':
            case 'onramp_api.load_error':
              onAlert?.(
                'Payment Error',
                `${data.data?.errorMessage ?? ''}`,
                'error',
              );
              setIsProcessingPayment?.(false);
              break;

            case 'onramp_api.commit_success':
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              onAlert?.(
                'Payment Successful!',
                'Your payment has been processed. Delivering crypto…',
                'success',
              );
              setTransactionStatus?.('pending');
              break;

            case 'onramp_api.polling_start':
              timeoutRef.current = setTimeout(() => {
                onAlert?.(
                  'Delivery Taking Longer',
                  'Crypto delivery is taking longer than expected.',
                  'info',
                );
              }, 300000); // 5 min
              break;

            case 'onramp_api.polling_success':
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              setTransactionStatus?.('success');
              onAlert?.(
                'Complete!',
                'Your crypto has been delivered!',
                'success',
              );
              break;

            case 'onramp_api.polling_error':
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              setTransactionStatus?.('error');
              onAlert?.(
                'Transaction Failed',
                data.data?.errorMessage ?? 'Blockchain error',
                'error',
              );
              break;

            default:
              console.log('Other event', data);
          }
        } catch (err) {
          console.log('ev.data', ev.data);
          console.error('Error parsing message', err);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }
      });
    };
    setupListener();

    return () => {
      if (listener) {
        (listener as PluginListenerHandle).remove();
      }
    };
  }, []);

  // nothing rendered — all happens natively
  return null;
}
